/**
 * MFA Verification Component
 * TOTP code input for multi-factor authentication
 */

import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, ArrowLeft, Shield, Clock } from 'lucide-react'

interface MfaVerificationProps {
  challengeType: 'totp' | 'sms' | 'email'
  expiresIn: number
  onVerify: (code: string) => Promise<void>
  onCancel: () => void
  isLoading: boolean
  error: string | null
}

export function MfaVerification({
  challengeType,
  expiresIn,
  onVerify,
  onCancel,
  isLoading,
  error,
}: MfaVerificationProps) {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [timeLeft, setTimeLeft] = useState(expiresIn)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1))
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft])

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        onVerify(fullCode)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      const newCode = pasted.split('')
      setCode(newCode)
      inputRefs.current[5]?.focus()
      onVerify(pasted)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const fullCode = code.join('')
    if (fullCode.length === 6) {
      onVerify(fullCode)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const isExpired = timeLeft <= 0

  return (
    <Card className="w-full max-w-md relative bg-white/95 backdrop-blur shadow-2xl">
      <CardHeader className="text-center pb-2">
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
          <Shield className="h-8 w-8 text-white" />
        </div>
        <CardTitle className="text-2xl font-bold text-gray-900">
          Two-Factor Authentication
        </CardTitle>
        <p className="text-gray-500 text-sm mt-1">
          {challengeType === 'totp'
            ? 'Enter the 6-digit code from your authenticator app'
            : `Enter the verification code sent to your ${challengeType}`}
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Expired warning */}
          {isExpired && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <Clock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Challenge expired. Please go back and try again.
              </p>
            </div>
          )}

          {/* Code input */}
          <div className="flex justify-center gap-2">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isLoading || isExpired}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-lg
                         focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none
                         disabled:bg-gray-100 disabled:cursor-not-allowed
                         transition-all duration-200"
              />
            ))}
          </div>

          {/* Timer */}
          {!isExpired && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>Code expires in {formatTime(timeLeft)}</span>
            </div>
          )}

          {/* Submit button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-2.5"
            disabled={isLoading || code.join('').length !== 6 || isExpired}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Verifying...
              </span>
            ) : (
              'Verify Code'
            )}
          </Button>

          {/* Back button */}
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 py-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </button>
        </form>

        {/* Help text */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            {challengeType === 'totp' ? (
              <>
                Open your authenticator app (Google Authenticator, Authy, etc.)
                <br />
                and enter the code shown for FirstHive.
              </>
            ) : (
              <>
                Didn't receive a code? Check your spam folder
                <br />
                or contact support for assistance.
              </>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export default MfaVerification
